import { memo } from 'react';

/**
 * §17 — Defensive shape polygon overlay for the 2D tactics board.
 *
 * Renders the convex-hull polygon of a team's "back six" outfielders
 * (computed in `@4cm/core/tactics/overlays.computeDefensiveShape`) as
 * a translucent fill on the pitch. The NFL coverage-shell idiom adapted
 * for soccer — gives the coach a glanceable read on how compact /
 * stretched the defensive block is.
 *
 * Coordinate mapping mirrors VoronoiLayer2D:
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 * Vertices are emitted in the same swap so the polygon lines up with
 * the underlying pitch markings and player tokens that share the
 * mapping.
 *
 * Style: team-coloured (home green / away amber) with baked opacity for
 * fill, and a darker stroke of the same family at constant 90% opacity
 * to keep the outline crisp regardless of the fill alpha.
 */

export interface DefensiveShapeHull {
  /** Polygon vertices in world metres. Empty array = no hull to draw
   *  (fewer than 3 eligible outfielders). */
  hull: { x: number; z: number }[];
}

export interface DefensiveShapeLayer2DProps {
  homeHull: DefensiveShapeHull;
  awayHull: DefensiveShapeHull;
  /** Caller-controlled opacity in [0, 1] — bakes into the fill rgba. */
  opacity: number;
}

/** Home green / away amber families — same swatches as PlayerToken2D so
 *  the shape reads as "the same team" at a glance. The stroke uses a
 *  darker shade at fixed high opacity for a crisp outline. */
function fillFor(team: 'home' | 'away', opacity: number): string {
  if (team === 'away') return `rgba(251, 191, 36, ${opacity})`;
  return `rgba(74, 222, 128, ${opacity})`;
}

function strokeFor(team: 'home' | 'away'): string {
  if (team === 'away') return 'rgba(180, 130, 10, 0.9)';
  return 'rgba(34, 130, 70, 0.9)';
}

function DefensiveShapeLayer2DInner({
  homeHull,
  awayHull,
  opacity,
}: DefensiveShapeLayer2DProps) {
  const homeHasShape = homeHull.hull && homeHull.hull.length >= 3;
  const awayHasShape = awayHull.hull && awayHull.hull.length >= 3;
  if (!homeHasShape && !awayHasShape) return null;

  const polygonFor = (
    hull: { x: number; z: number }[],
    team: 'home' | 'away',
  ) => {
    // World (x, z) → SVG (-z, x) per the landscape mapping.
    const points = hull.map((v) => `${-v.z},${v.x}`).join(' ');
    return (
      <polygon
        key={team}
        points={points}
        fill={fillFor(team, opacity)}
        stroke={strokeFor(team)}
        strokeWidth={0.2}
        strokeLinejoin="round"
        data-team={team}
        data-testid={`t2d-defshape-${team}`}
      />
    );
  };

  return (
    <g
      className="t2d-defshape-layer"
      data-testid="t2d-defshape-layer"
      pointerEvents="none"
      // Clipped to the pitch rectangle — convex hull vertices sit on
      // player positions which are always in-bounds, but the polygon
      // edges between hull vertices can graze the touchline if a back
      // line is wide. See VoronoiLayer2D + PitchSvg for the def.
      clipPath="url(#t2d-pitch-clip)"
    >
      {homeHasShape && polygonFor(homeHull.hull, 'home')}
      {awayHasShape && polygonFor(awayHull.hull, 'away')}
    </g>
  );
}

/** Memoised — hulls are recomputed only when the back-six positions
 *  shift, so the parent typically hands stable hull arrays across
 *  unrelated HUD-state renders. */
export const DefensiveShapeLayer2D = memo(DefensiveShapeLayer2DInner);
